import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import type { ReportType } from 'pages/bugs-features/types';
import {
  isReportFormOpenAtom,
  reportFormErrorAtom,
  reportFormInitialTypeAtom,
  type ReportFormError,
} from './atoms';

/**
 * Opens the shared report form.
 *
 * The form is mounted once at the app shell, so every button that offers to file something
 * is really setting the same atoms. Three places were doing that by hand — the header's two
 * icons, the feedback page's Submit button, and the error page — and each knew a slightly
 * different amount about what needed setting.
 *
 * That is the reason this exists, rather than tidiness. `reportFormErrorAtom` is set by the
 * error page and read on submit, and none of the other openers cleared it: the invariant
 * "the crash attached to this report is the crash you are looking at" was being held up by
 * ReportFormHost clearing on close, and would have broken the first time an opener was
 * added that did not know to care. Setting it on every open — to null unless a crash is
 * passed — makes it a property of opening the form rather than of remembering to clean up.
 *
 * @return {Function} Call with the kind to file, and optionally the crash to attach.
 */
export function useOpenReportForm() {
  const setOpen = useSetAtom(isReportFormOpenAtom);
  const setType = useSetAtom(reportFormInitialTypeAtom);
  const setCrash = useSetAtom(reportFormErrorAtom);

  return useCallback(
    (type: ReportType = 'bug', crash: ReportFormError | null = null) => {
      setType(type);
      setCrash(crash);
      setOpen(true);
    },
    [setType, setCrash, setOpen]
  );
}
